import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDeviceStatus } from "../hooks/useDeviceStatus";
import { setDeviceChecksSnapshot } from "../utils/deviceChecks";

// Browser fitness for onboarding (2026-10-08). Measures silently and renders
// NOTHING when the browser is fit. When it is not, it tells the participant
// why and what to do. Only facts about the browser/device the page is open
// on — nothing about the WhatsApp phone, which a web page cannot see.
// Offline is handled by NetworkBanner, so it is captured here but not shown.
// Every measurement is snapshotted into donation_context.extra.device_checks
// with the registration request for the pilot audit.

const BrowserCheck = () => {
    const { t } = useTranslation();
    const { network, battery, env } = useDeviceStatus();

    const problems = [];
    if (env.inAppBrowser) problems.push({ key: "inApp", level: "block" });
    if (env.cookiesEnabled === false) problems.push({ key: "cookies", level: "block" });
    if (env.isMobile) problems.push({ key: "onPhone", level: "block" });
    if (network.online && network.status === "bad") problems.push({ key: "slowNetwork", level: "warn" });
    else if (network.online && network.status === "warn") problems.push({ key: "weakNetwork", level: "warn" });
    if (battery.status === "bad") problems.push({ key: "lowBattery", level: "warn" });

    useEffect(() => {
        setDeviceChecksSnapshot({
            page_device: env.isMobile ? "mobile" : "desktop",
            in_app_browser: env.inAppBrowser,
            cookies_enabled: env.cookiesEnabled,
            network_status: network.status, network_online: network.online, network_effective_type: network.effectiveType, network_save_data: network.saveData,
            battery_status: battery.status, battery_level: battery.level, battery_charging: battery.charging,
            problems: problems.map((p) => p.key),
            fit: !problems.some((p) => p.level === "block"),
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [env, network, battery, problems.map((p) => p.key).join(",")]);

    if (problems.length === 0) return null;

    const blocking = problems.some((p) => p.level === "block");
    const box = {
        background: blocking ? "#fdf1f0" : "#fff8e8",
        border: `1px solid ${blocking ? "#e5a9a3" : "#ecd39a"}`,
        borderRadius: 10, padding: "14px 18px", margin: "0 0 20px",
    };
    return (
        <div className="browser-check" role="alert" style={box}>
            <strong>{t(blocking ? "browserCheck.titleBlock" : "browserCheck.titleWarn")}</strong>
            <ul style={{ margin: "10px 0 0", paddingLeft: 20, lineHeight: 1.6 }}>
                {problems.map((p) => (
                    <li key={p.key}>
                        <strong>{t(`browserCheck.${p.key}.reason`)}</strong> {t(`browserCheck.${p.key}.fix`)}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default BrowserCheck;
